import React from 'react';

import { CSSObject } from '@emotion/react';
import styled from '@emotion/styled';

import { ArrowIcon } from '../../Icons';

interface Props {
  children: string | React.ReactElement;
  onClick: (e?: any) => void;
  design: 'white' | 'black' | 'gray' | 'palegray' | 'filled' | 'outline' | 'active' | 'inactive';
  icon?: 'back' | 'forward';
  style?: CSSObject;
  disabled?: boolean;
  disableRipple?: boolean;
  radius?: string;
  $isFormikDirty?: boolean;
  $isFormikError?: boolean;
  $fontColor?: string;
}

const RoundBtn = React.forwardRef<HTMLButtonElement, Props>(
  (
    { children, onClick, design, icon, style, $isFormikDirty, $isFormikError, $fontColor, radius, disabled, ...rest },
    ref
  ) => {
    return (
      <SButton
        type="button"
        onClick={onClick}
        ref={ref}
        disabled={disabled}
        $design={design}
        $radius={radius}
        $isFormikDirty={$isFormikDirty}
        $isFormikError={$isFormikError}
        $fontColor={$fontColor}
        style={style as React.CSSProperties}
        {...rest}
      >
        {icon === 'back' && (
          <span className="arrow">
            <ArrowIcon />
          </span>
        )}
        {children}
        {icon === 'forward' && (
          <span className="arrow forward">
            <ArrowIcon />
          </span>
        )}
      </SButton>
    );
  }
);

export default RoundBtn;

const SButton = styled.button<{
  $design: Props['design'];
  $radius?: string;
  $isFormikDirty?: boolean;
  $isFormikError?: boolean;
  $fontColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 32px;
  vertical-align: baseline;
  border-radius: ${({ $radius }) => $radius ?? '15px'};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.static.black};
  font-family: Poppins, sans-serif;
  text-transform: initial;
  font-size: 16px;
  cursor: pointer;

  span {
    line-height: 1;
  }

  .arrow {
    height: 12px;
  }

  .forward {
    margin-left: 4px;
    line-height: 1;
    transform: rotate(180deg);

    use {
      fill: ${({ theme, $design }) =>
        $design === 'filled' || $design === 'inactive' ? theme.color.static.white : theme.color.static.primary};
    }
  }

  ${({ $design, theme }) =>
    $design === 'white' &&
    `
    color: ${theme.color.static.white};
    border: 1px solid ${theme.color.static.white};
  `}

  ${({ $design, theme }) =>
    $design === 'black' &&
    `
    color: ${theme.color.text.TXT_3};
    border: 1px solid ${theme.color.text.TXT_3};
  `}

  ${({ $design, theme }) =>
    $design === 'gray' &&
    `
    color: #909090;
    border: 1px solid ${theme.palette.neutral[30]};
    font-weight: normal;
  `}

  ${({ $design }) =>
    $design === 'palegray' &&
    `
    color: #909090;
    border: 1px solid #f1f1f4;
    font-weight: normal;
  `}

  ${({ $design, theme }) =>
    $design === 'outline' &&
    `
    color: ${theme.color.static.primary};
    border: 1px solid ${theme.color.static.primary};
  `}

  ${({ $design, theme }) =>
    $design === 'filled' &&
    `
    color: ${theme.color.label.onAccent};
    background-color: ${theme.color.static.primary};
    font-weight: 600;
  `}

  ${({ $design, $isFormikDirty, $isFormikError, theme }) =>
    $design === 'active' &&
    `
    border: 1px solid ${theme.color.static.primary};
    color: ${!$isFormikError && $isFormikDirty ? theme.color.label.onAccent : theme.color.static.primary};
    background-color: ${!$isFormikError && $isFormikDirty ? theme.color.static.primary : theme.color.static.white};
    font-weight: ${!$isFormikError && $isFormikDirty ? '600' : 'normal'};
  `}

  ${({ $design, theme }) =>
    $design === 'inactive' &&
    `
    background-color: ${theme.color.buttons.disable};
    color: ${theme.color.label.primary};
    font-weight: 600;
  `}

  &:disabled {
    cursor: not-allowed;
    pointer-events: initial;
    background-color: ${({ theme }) => theme.color.buttons.disable};
    color: ${({ theme }) => theme.color.label.primary};
  }

  ${({ $fontColor }) => $fontColor && `color: ${$fontColor};`}
`;
